import axios from 'axios';
import { API_URL } from '../utils/apiConfig';
import { 
  invalidateDocumentQueries, 
  invalidateSignatureQueries,
  optimisticallyUpdateDocumentStatus 
} from '../utils/queryClient';

// Determine if we're using the deployed backend
// const isDeployedBackend = API_URL.includes('signme-backend-92668879435.us-central1.run.app');
const isDeployedBackend = false;

// Create axios instance with the correct base URL
const api = axios.create({
  baseURL: API_URL
});

// Log the base URL for debugging
console.log('API Base URL:', API_URL);

// Helper function to get token
const getToken = () => {
  // First try to get token directly from localStorage
  const token = localStorage.getItem('token');
  if (token) return token;
  
  // If not found, try to get from user object
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      if (user.token) {
        // Save token separately for future use
        localStorage.setItem('token', user.token);
        return user.token;
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
  }
  return null;
};

// Add auth token to all requests
api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const signatureService = {
  // Get all signatures for a document with signed URLs
  async getDocumentSignatures(documentId, signatureToken = null) {
    if (!documentId) {
      console.warn('No document ID provided to getDocumentSignatures');
      return { signatures: [] };
    }

    try {
      const url = signatureToken 
        ? `/signatures/document/${documentId}?token=${signatureToken}`
        : `/signatures/document/${documentId}`;
      
      const response = await api.get(url);
      
      if (!response.data || !response.data.signatures) {
        console.warn('No signatures found in response');
        return { signatures: [] };
      }

      const { signatures } = response.data;

      // Process each signature to get its signed URL
      const processedSignatures = await Promise.all(
        signatures.map(async (signature) => {
          try {
            // Build URL for signature image with optional token
            let imageUrl = `/signatures/${signature.id}/image`;
            if (signatureToken) {
              imageUrl += `?token=${signatureToken}`;
            }
            
            // Get the signed URL for the signature image
            const imageResponse = await api.get(imageUrl, {
              responseType: 'blob'
            });
            
            // Create a blob URL from the image data
            const blob = new Blob([imageResponse.data], { type: 'image/png' });
            const signatureUrl = URL.createObjectURL(blob);
            
            // Store cleanup function for later use
            const cleanup = () => URL.revokeObjectURL(signatureUrl);
            
            return {
              ...signature,
              signatureUrl,
              cleanup,
              isProcessing: false
            };
          } catch (error) {
            console.error(`Error getting signature image for signature ${signature.id}:`, error);
            return {
              ...signature,
              signatureUrl: null,
              isProcessing: true,
              retryAfter: Date.now() + 2000 // Retry after 2 seconds
            };
          }
        })
      );

      return {
        ...response.data,
        signatures: processedSignatures
      };
    } catch (error) {
      console.error('Error fetching document signatures:', error);
      return { signatures: [] };
    }
  },
  
  // Get a single signature by ID with signed URL
  async getSignature(signatureId) {
    try {
      const response = await api.get(`/signatures/${signatureId}`);
      
      // If the signature doesn't have a signatureUrl, request one
      if (response.data && !response.data.signatureUrl && response.data.signatureFile) {
        try {
          const urlResponse = await api.get(`/documents/signature/${signatureId}/signed-url`);
          response.data.signatureUrl = urlResponse.data.signedUrl;
        } catch (err) {
          console.error(`Error getting signed URL for signature ${signatureId}:`, err);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching signature:', error);
      throw error;
    }
  },
  
  // Get a signed URL for a signature
  async getSignatureSignedUrl(signatureId) {
    try {
      const response = await api.get(`/documents/signature/${signatureId}/signed-url`);
      return response.data.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL for signature:', error);
      throw error;
    }
  },
  
  // Get the URL for a signature image with authentication
  getSignatureImageUrl(signatureId, signatureToken = null) {
    try {
      if (!signatureId) return null;
      
      // If signature token is provided, use it; otherwise try to get JWT token
      let authParam = '';
      if (signatureToken) {
        authParam = `?token=${encodeURIComponent(signatureToken)}`;
      } else {
        const token = getToken();
        if (!token) return null;
        authParam = `?token=${encodeURIComponent(token)}`;
      }
      
      return `${API_URL}/signatures/${signatureId}/image${authParam}`;
    } catch (error) {
      console.error('Error generating signature URL:', error);
      return null;
    }
  },
  
  // Sign a document
  async signDocument(documentId, signatures, requestId = null, signatureToken = null) {
    try {
      let url = `/documents/${documentId}/sign`;
      
      // Add query parameters
      const params = new URLSearchParams();
      if (requestId) {
        params.append('requestId', requestId);
      }
      if (signatureToken) {
        params.append('token', signatureToken);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log('Signing document with data:', { documentId, signatures, requestId });
      
      // Format signatures array as expected by the backend with precise coordinates
      const formattedSignatures = Array.isArray(signatures) ? 
        signatures.map(sig => {
          // Use the coordinates that were already properly formatted in DocumentViewer
          // DO NOT override them here - they come from the signature request
          console.log(`Using coordinates from DocumentViewer for fieldId ${sig.fieldId}:`, sig.coordinates);
          
          return {
            signatureType: sig.type,
            signatureData: sig.data,
            coordinates: sig.coordinates, // Use the coordinates exactly as passed from DocumentViewer
            fieldId: sig.fieldId
          };
        }) : [];
      
      console.log('Formatted signatures for backend:', formattedSignatures);
      
      const requestData = {
        signatures: formattedSignatures
      };
      
      console.log('✍️ Making request to:', url);
      console.log('✍️ Request data:', requestData);
      
      const response = await api.post(url, requestData);
      console.log('✍️ Sign document response:', response.data);
      
      // ✅ CACHE INVALIDATION: Invalidate both documents and signatures after signing
      invalidateDocumentQueries();
      invalidateSignatureQueries(documentId);
      
      // 🚀 OPTIMISTIC UPDATE: Update document status if completed
      if (response.data?.status === 'completed') {
        optimisticallyUpdateDocumentStatus(documentId, 'completed');
      }
      
      console.log('🔄 Cache invalidated after document signing');
      
      return response.data;
    } catch (error) {
      console.error('Error signing document:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },
  
  async createSignatureRequest(documentId, signers, message = null, additionalData = {}) {
  console.log('===== signatureService createSignatureRequest =====');
  console.log('Document ID:', documentId);
  console.log('Signers data:', JSON.stringify(signers, null, 2));
  console.log('Message:', message);
  console.log('Additional data:', additionalData);
  
  // Validate input
  if (!documentId) {
    console.error('No document ID provided');
    throw new Error('Document ID is required');
  }
  
  if (!signers || !Array.isArray(signers) || signers.length === 0) {
    console.error('Invalid signers data:', signers);
    throw new Error('Signers must be a non-empty array');
  }
  
  // Check first signer's structure
  const firstSigner = signers[0];
  console.log('First signer structure:', Object.keys(firstSigner));
  
  if (!firstSigner.email) {
    console.error('Signer missing email');
    throw new Error('Each signer must have an email');
  }
  
  // Check for signature fields with different possible property names
  const hasFields = 
    (firstSigner.fields && Array.isArray(firstSigner.fields) && firstSigner.fields.length > 0) ||
    (firstSigner.signatureFields && Array.isArray(firstSigner.signatureFields) && firstSigner.signatureFields.length > 0);
  
  if (!hasFields) {
    console.error('Signer missing signature fields');
    throw new Error('Each signer must have signature fields');
  }
  
  try {
    // For deployed backend with CORS issues, use a mock response
    if (isDeployedBackend) {
      console.log('Deployed backend detected - using mock response');
      
      // Return a mock successful response
      return {
        success: true,
        message: 'Mock signature request created (CORS workaround)',
        documentId,
        signers,
        mockResponse: true,
        requestId: `mock-${Date.now()}-${documentId.substring(0, 8)}`,
        createdAt: new Date().toISOString()
      };
    }
    
    // Format data for the endpoint exactly as expected
    // Try to use 'signatureFields' property name explicitly
    const formattedSigners = signers.map(signer => {
      const signatureFields = signer.signatureFields || signer.fields || [];
      
      return {
        email: signer.email,
        name: signer.name || signer.email.split('@')[0],
        signatureFields: signatureFields
      };
    });
    
    console.log('Formatted signers for API:', JSON.stringify(formattedSigners, null, 2));
    
    // Make the API call to the correct endpoint
    const endpointUrl = `/signatures/requests/${documentId}`;
    console.log('📋 Calling endpoint:', endpointUrl);
    
    const response = await api.post(endpointUrl, {
      signers: formattedSigners,
      message: message,
      title: additionalData.title,
      expirationDate: additionalData.expirationDate
    });
    
    console.log('📋 API response:', response.data);
    
    // ✅ CACHE INVALIDATION: Invalidate documents after signature request creation
    invalidateDocumentQueries();
    
    // 🚀 OPTIMISTIC UPDATE: Update document status to pending_signature
    optimisticallyUpdateDocumentStatus(documentId, 'pending_signature');
    
    console.log('🔄 Cache invalidated after signature request creation');
    
    return response.data;
  } catch (error) {
    console.error('Error details:', error.response?.data || error.message);
    
    // Check if this is a CORS error
    if (error.message && error.message.includes('Network Error')) {
      console.error('Possible CORS error detected');
      
      // Return a mock successful response
      return {
        success: true,
        message: 'Mock signature request created (CORS workaround)',
        documentId,
        signers,
        mockResponse: true,
        requestId: `mock-${Date.now()}-${documentId.substring(0, 8)}`,
        createdAt: new Date().toISOString()
      };
    }
    
    throw error;
  }
},
  
  // Mark a signature request as viewed
  async viewSignatureRequest(requestId) {
    try {
      const response = await api.patch(`/documents/signatures/${requestId}/view`);
      return response.data;
    } catch (error) {
      console.error('Error marking signature request as viewed:', error);
      return {
        success: false,
        error: error.message || 'Failed to view signature request'
      };
    }
  },
  
  // Decline a signature request
  async declineSignatureRequest(requestId, reason) {
    try {
      const response = await api.post(`/documents/signatures/${requestId}/decline`, {
        reason
      });
      return response.data;
    } catch (error) {
      console.error('Error declining signature request:', error);
      throw error;
    }
  },
  
  // Send a reminder for a signature request
  async sendSignatureReminder(requestId) {
    try {
      const response = await api.post(`/documents/signatures/${requestId}/remind`);
      return response.data;
    } catch (error) {
      console.error('Error sending signature reminder:', error);
      throw error;
    }
  },
  
  // Delete a signature
  async deleteSignature(signatureId) {
    try {
      const response = await api.delete(`/signatures/${signatureId}`);
      
      // ✅ CACHE INVALIDATION: Invalidate documents and signatures after deletion
      invalidateDocumentQueries();
      invalidateSignatureQueries();
      console.log('🔄 Cache invalidated after signature deletion');
      
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting signature:', error);
      throw error;
    }
  },
  
  // Get pending signature requests for the current user
  async getPendingSignatureRequests() {
    try {
      const response = await api.get('/signatures/requests/pending');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending signature requests:', error);
      throw error;
    }
  },
  
  // Get signature requests sent by the current user
  async getSentSignatureRequests() {
    try {
      try {
        // Try the backend endpoint first
        const response = await api.get('/signatures/requests/sent');
        return response.data;
      } catch (backendError) {
        console.warn('Backend endpoint for sent signature requests not available:', backendError);
        
        // Return mock data until backend is ready
        console.log('Using mock data for signature requests');
        
        return []; // Empty array for now
      }
    } catch (error) {
      console.error('Error fetching sent signature requests:', error);
      throw error;
    }
  },
  
  // Cancel a signature request
  async cancelSignatureRequest(requestId) {
    try {
      const response = await api.delete(`/documents/signatures/${requestId}`);
      
      // ✅ CACHE INVALIDATION: Invalidate documents after signature request cancellation
      invalidateDocumentQueries();
      console.log('🔄 Cache invalidated after signature request cancellation');
      
      return response.data;
    } catch (error) {
      console.error('❌ Error canceling signature request:', error);
      throw error;
    }
  },
  
  // Diagnostic function to discover API endpoints
  async discoverApiEndpoints(documentId) {
    // For deployed backend, just show a message about CORS
    if (isDeployedBackend) {
      console.log('CORS Check: Your frontend is running on localhost while backend is on a different domain');
      console.log('This often causes CORS issues with OPTIONS preflight requests');
      console.log('Solution options:');
      console.log('1. Run both frontend and backend locally during development');
      console.log('2. Configure the backend to properly handle CORS requests');
      console.log('3. Use a CORS proxy during development');
      
      alert('CORS issues detected with the deployed backend. Check console for details.');
      
      return { message: 'CORS check completed' };
    }
    
    const endpoints = [
      `/documents/${documentId}/signatures`,
      `/documents/${documentId}/signature-requests`,
      `/signature-requests`,
      `/documents/${documentId}/sign-request`,
      `/api/documents/${documentId}/signatures`,
      `/documents/signature/${documentId}/signed-url`
    ];
    
    console.log('Running API endpoint discovery...');
    
    for (const endpoint of endpoints) {
      try {
        console.log(`Testing endpoint: ${API_URL}${endpoint}`);
        // Use GET instead of OPTIONS for testing, as it's less likely to trigger CORS
        const response = await axios.get(`${API_URL}${endpoint}`, {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          },
          validateStatus: (status) => status < 500 // Accept any status code below 500
        });
        console.log(`Endpoint ${endpoint} status:`, response.status);
        if (response.status < 400) {
          console.log('ENDPOINT FOUND:', endpoint);
        }
      } catch (error) {
        const isCorsError = error.message && (
          error.message.includes('Network Error') || 
          error.message.includes('CORS')
        );
        
        console.log(`Endpoint ${endpoint} error:`, error.message);
        if (isCorsError) {
          console.log('CORS issue detected with this endpoint');
        }
      }
    }
    
    return { message: 'Endpoint discovery completed' };
  }
};

export default signatureService;